import React, { useState, useEffect } from 'react'
    import { motion, AnimatePresence } from 'framer-motion'
    import { useAuth } from '../../contexts/AuthContext'
    import SafeIcon from '../../common/SafeIcon'
    import * as FiIcons from 'react-icons/fi'
    import toast from 'react-hot-toast'

    const { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiArrowLeft, FiLoader, FiCheckCircle } = FiIcons

    const AuthForm = ({ onBack }) => {
      const [isSignUp, setIsSignUp] = useState(false)
      const [showPassword, setShowPassword] = useState(false)
      const [loading, setLoading] = useState(false)
      const [isInvited, setIsInvited] = useState(false)
      const [isSuccess, setIsSuccess] = useState(false)
      const [formData, setFormData] = useState({ email: '', password: '', fullName: '' })
      const { signIn, signUp } = useAuth()

      // Handle Invitation Parameters in URL
      useEffect(() => {
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
        const emailParam = urlParams.get('email');
        const inviteParam = urlParams.get('invite');

        if (emailParam) {
          setFormData(prev => ({ ...prev, email: emailParam }));
        }

        if (inviteParam === 'true') {
          setIsSignUp(true);
          setIsInvited(true);
          toast.success("Invitation detected! Please complete your registration.", {
            id: 'invite-detected',
            duration: 5000
          });
        }
      }, []);

      const handleSubmit = async (e) => {
        e.preventDefault()
        setLoading(true)
        try {
          if (isSignUp) {
            if (formData.password.length < 6) {
              throw new Error('Password must be at least 6 characters')
            }
            const { error } = await signUp(formData.email, formData.password, formData.fullName)
            if (error) throw error
            
            setIsSuccess(true)
            toast.success('Account created! Welcome to Zora\'s House.')
          } else {
            const { error } = await signIn(formData.email, formData.password)
            if (error) throw error
            
            setIsSuccess(true)
            toast.success('Welcome back to Zora\'s House!')
          }
        } catch (error) {
          toast.error(error.message)
        } finally {
          setLoading(false)
        }
      }

      const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
      }

      if (isSuccess) {
        return (
          <div className="min-h-screen bg-space_indigo flex items-center justify-center p-4 relative overflow-hidden">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }} 
              animate={{ opacity: 1, scale: 1 }} 
              className="bg-white rounded-[2.5rem] shadow-2xl border border-white-400 w-full max-w-md p-12 text-center relative z-10"
            >
              <div className="bg-dark_cyan-900 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-8">
                <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-dark_cyan" />
              </div>
              <h2 className="text-3xl font-black text-space_indigo uppercase tracking-tight mb-4">Welcome Home</h2>
              <p className="text-white-200 font-bold text-sm uppercase tracking-widest leading-relaxed">
                Your account is ready. Redirecting you to the library shelves...
              </p>
            </motion.div>
          </div>
        )
      }

      return (
        <div className="min-h-screen bg-space_indigo flex items-center justify-center p-4 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-dark_cyan/10 rounded-full blur-[120px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-hot_berry/10 rounded-full blur-[120px]" />
          </div>

          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="bg-white rounded-[2.5rem] shadow-2xl border border-white-400 w-full max-w-md p-8 md:p-10 relative z-10"
          >
            <button 
              onClick={onBack} 
              className="absolute left-8 top-8 text-white-300 hover:text-dark_cyan transition-colors flex items-center text-[10px] font-black uppercase tracking-[0.2em]"
            >
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" /> Back
            </button>

            <div className="text-center mb-10 mt-6">
              <motion.div 
                initial={{ y: -20 }} 
                animate={{ y: 0 }} 
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl ${isInvited ? 'bg-dark_cyan' : 'bg-gradient-to-br from-dark_cyan to-hot_berry'}`}
              >
                <SafeIcon icon={isInvited ? FiCheckCircle : FiUser} className="w-8 h-8 text-white" />
              </motion.div>
              <h2 className="text-2xl font-black text-space_indigo uppercase tracking-tight">
                {isInvited ? "Member Access" : isSignUp ? "Create Account" : 'Library Login'}
              </h2>
              <p className="text-white-300 mt-2 font-black text-[10px] uppercase tracking-[0.15em]">
                {isInvited ? 'Complete your community registration' : isSignUp ? 'Join our community library' : 'Sign in to manage your shelf'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <AnimatePresence mode="wait">
                {isSignUp && (
                  <motion.div 
                    key="signup-fields" 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: 'auto' }} 
                    exit={{ opacity: 0, height: 0 }} 
                    className="space-y-6 overflow-hidden"
                  >
                    <div>
                      <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Full Name</label>
                      <div className="relative">
                        <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white-400 w-4 h-4" />
                        <input 
                          type="text" 
                          name="fullName" 
                          required={isSignUp} 
                          value={formData.fullName} 
                          onChange={handleInputChange} 
                          className="w-full pl-11 pr-4 py-4 bg-white-500 border-2 border-white-400 rounded-2xl focus:border-dark_cyan transition-all text-space_indigo font-bold text-sm" 
                          placeholder="Jane Doe" 
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Email Address</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white-400 w-4 h-4" />
                  <input 
                    type="email" 
                    name="email" 
                    required 
                    readOnly={isInvited} 
                    value={formData.email} 
                    onChange={handleInputChange} 
                    className={`w-full pl-11 pr-4 py-4 border-2 rounded-2xl focus:border-dark_cyan transition-all text-space_indigo font-bold text-sm ${isInvited ? 'bg-white-500 border-white-400 cursor-not-allowed opacity-70' : 'bg-white-500 border-white-400'}`} 
                    placeholder="jane@example.com" 
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-white-300 uppercase tracking-widest mb-2 ml-1">Password</label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white-400 w-4 h-4" />
                  <input 
                    type={showPassword ? 'text' : 'password'} 
                    name="password" 
                    required 
                    value={formData.password} 
                    onChange={handleInputChange} 
                    className="w-full pl-11 pr-12 py-4 bg-white-500 border-2 border-white-400 rounded-2xl focus:border-dark_cyan transition-all text-space_indigo font-bold text-sm" 
                    placeholder="••••••••" 
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)} 
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white-300 hover:text-dark_cyan transition-colors"
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <motion.button 
                whileHover={{ scale: 1.01, y: -2 }} 
                whileTap={{ scale: 0.99 }} 
                type="submit" 
                disabled={loading} 
                className="w-full bg-space_indigo text-white py-4 rounded-2xl font-black uppercase tracking-[0.2em] text-[10px] shadow-xl shadow-space_indigo/20 hover:bg-space_indigo-400 disabled:opacity-50 transition-all flex items-center justify-center border-2 border-space_indigo"
              >
                {loading ? <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" /> : isInvited ? 'Complete Membership' : isSignUp ? 'Create My Account' : 'Sign In To Library'}
              </motion.button>
            </form>

            {!isInvited && (
              <div className="mt-10 pt-8 border-t-2 border-white-400 text-center">
                <p className="text-white-300 font-black text-[10px] uppercase tracking-widest">
                  {isSignUp ? 'Already a member?' : "New to the house?"} 
                  <button onClick={() => setIsSignUp(!isSignUp)} className="ml-2 text-hot_berry hover:underline transition-all">
                    {isSignUp ? 'Sign In Instead' : 'Join Now'}
                  </button>
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )
    }

    export default AuthForm